import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { BRAND } from "../../data/project";

const NAV = [
  { label: "Estate", id: "overview" },
  { label: "Collection", id: "collection" },
  { label: "Investment", id: "investment" },
  { label: "Plan", id: "payment" },
  { label: "Location", id: "location" },
  { label: "Process", id: "process" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const inkLight = !scrolled; // light typography when over hero
  const textCls = inkLight ? "text-[#F4F2EC]" : "text-[#1A3626]";
  const hoverCls = "hover:text-[#B3906A]";

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#F4F2EC]/90 backdrop-blur-xl border-b hairline"
          : "bg-gradient-to-b from-[#1A3626]/40 to-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 h-20 md:h-24 flex items-center justify-between">
        <a
          href="#top"
          data-testid="header-logo"
          className="flex items-center"
          aria-label="PK Properties"
        >
          <span className="block h-16 md:h-20 w-[170px] md:w-[220px] overflow-hidden flex items-center justify-start">
            <img
              src={BRAND.agencyLogo}
              alt="PK Properties"
              className={`h-full w-auto object-contain origin-left transition-all duration-500 ${
                inkLight ? "invert brightness-200" : ""
              }`}
              style={{ transform: "scale(2.2)", transformOrigin: "left center" }}
            />
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((n) => (
            <button
              key={n.id}
              data-testid={`nav-${n.id}`}
              onClick={() => scrollTo(n.id)}
              className={`text-[12px] font-medium tracking-[0.22em] uppercase ${textCls} ${hoverCls} transition-colors`}
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={`tel:${BRAND.phone.replace(/\s/g, "")}`}
            data-testid="header-phone"
            className={`flex items-center gap-2 text-[12px] tracking-[0.18em] uppercase ${textCls} ${hoverCls} transition-colors`}
          >
            <Phone className="w-3.5 h-3.5" strokeWidth={1.5} />
            {BRAND.phone}
          </a>
          <button
            data-testid="header-cta"
            onClick={() => scrollTo("contact")}
            className={inkLight ? "hge-btn-light" : "hge-btn-primary"}
          >
            Register Interest
          </button>
        </div>

        <button
          data-testid="header-mobile-toggle"
          onClick={() => setOpen(!open)}
          className={`lg:hidden ${textCls}`}
          aria-label="Menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-[#F4F2EC] border-t hairline"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV.map((n) => (
              <button
                key={n.id}
                data-testid={`mobile-nav-${n.id}`}
                onClick={() => scrollTo(n.id)}
                className="text-left text-[12px] tracking-[0.22em] uppercase text-[#1A3626]"
              >
                {n.label}
              </button>
            ))}
            <button
              data-testid="mobile-cta"
              onClick={() => scrollTo("contact")}
              className="hge-btn-primary mt-2 w-fit"
            >
              Register Interest
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
